import { toNavGroupName } from '../../hooks/useSpacesNavItems';
import type { LayoutSection, LayoutSpec } from '../../types/LayoutConfig';

/** URL slug for `layoutId` from `LayoutSpec.group`, if any (matches grouped route segments). */
export function getNavGroupSlug(layouts: readonly LayoutSpec[] | undefined, layoutId: string): string | undefined {
  const groupLabel = layouts?.find(l => l.id === layoutId)?.group;
  return groupLabel != null && groupLabel !== '' ? toNavGroupName(groupLabel) : undefined;
}

/**
 * Canonical URL base for a space: `/:section/:navGroupName/:layoutId` when grouped, else `/:section/:layoutId`.
 * `navGroupName` is the path segment (slug), not the display label.
 */
export function getSpaceBasePath(
  layoutSection: LayoutSection,
  navGroupName: string | undefined,
  layoutId: string
): string {
  if (navGroupName != null && navGroupName !== '') {
    return `/${layoutSection}/${navGroupName}/${layoutId}`;
  }
  return `/${layoutSection}/${layoutId}`;
}

/**
 * Prefix of `pathname` that corresponds to the space for `layoutId` (through that segment),
 * e.g. `/trading/group-slug/uuid` or `/trading/uuid`.
 */
export function getSpacePathPrefix(
  pathname: string,
  layoutSection: LayoutSection,
  layoutId: string
): string | undefined {
  const prefix = `/${layoutSection}/`;
  if (!pathname.startsWith(prefix)) {
    return undefined;
  }
  const rest = pathname.slice(prefix.length);
  const segments = rest.split('/').filter(Boolean);
  const layoutIndex = segments.indexOf(layoutId);
  if (layoutIndex === -1) {
    return undefined;
  }
  return `/${layoutSection}/${segments.slice(0, layoutIndex + 1).join('/')}`;
}

/**
 * Resolves which space `layoutId` the user is on for URLs under `/layoutSection/...`.
 * Uses `/section/navGroupName/layoutId/...` when the second segment is a known layout id; otherwise
 * `/section/layoutId/...` for ungrouped spaces (avoids misreading grouped URLs when slugs collide with ids).
 */
export function parseLayoutIdFromSectionPath(
  pathname: string,
  layoutSection: LayoutSection,
  layoutIds: ReadonlySet<string>
): string | undefined {
  const prefix = `/${layoutSection}/`;
  if (!pathname.startsWith(prefix)) {
    return undefined;
  }
  const rest = pathname.slice(prefix.length);
  const segments = rest.split('/').filter(Boolean);
  if (segments.length === 0) {
    return undefined;
  }
  if (segments.length >= 2 && layoutIds.has(segments[1])) {
    return segments[1];
  }
  if (layoutIds.has(segments[0])) {
    return segments[0];
  }
  return undefined;
}

export interface PathUpdateAfterSpaceGroupChange {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * If the user is viewing a space under `layoutSection` and `layouts` implies a different base path
 * than the current URL (e.g. moved the active space into/out of a named group), returns the location to navigate to.
 */
export function getLocationAfterSpaceGroupChange(
  pathname: string,
  search: string,
  hash: string,
  layoutSection: LayoutSection,
  layouts: readonly LayoutSpec[]
): PathUpdateAfterSpaceGroupChange | null {
  const layoutIds = new Set(layouts.map(l => l.id));
  const currentLayoutId = parseLayoutIdFromSectionPath(pathname, layoutSection, layoutIds);
  if (!currentLayoutId) {
    return null;
  }
  const oldPrefix = getSpacePathPrefix(pathname, layoutSection, currentLayoutId);
  const navGroupSlug = getNavGroupSlug(layouts, currentLayoutId);
  const newBase = getSpaceBasePath(layoutSection, navGroupSlug, currentLayoutId);
  if (!oldPrefix || !pathname.startsWith(oldPrefix)) {
    return null;
  }
  const newPathname = newBase + pathname.slice(oldPrefix.length);
  if (newPathname === pathname) {
    return null;
  }
  return { pathname: newPathname, search, hash };
}
